import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { ImageWithFallback } from '../../components/common/ImageWithFallback';
import {
  listPictureTalkScenes,
  displaySceneTitle,
  type PictureTalkSceneListItem,
} from '../../modules/learning/api/pictureTalk';
import { useStudentMe } from '../../modules/roster/hooks/useStudentMe';
import { LearningHttpError } from '../../modules/learning/api/stories';
import { studentPictureTalkScenePath } from '../../utils/constants';
import { handleSchoolFeatureDisabledIfPresent } from '../../utils/schoolFeatureDisabledStudent';

const PictureTalkListPage: React.FC = () => {
  const navigate = useNavigate();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();
  const [scenes, setScenes] = useState<PictureTalkSceneListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { student, grade, isRosterLoading, authLoading } = useStudentMe();

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);

    const studentGrade = grade;
    const validGrade =
      Number.isFinite(Number(studentGrade)) && (studentGrade as number) >= 1 && (studentGrade as number) <= 12
        ? Number(studentGrade)
        : undefined;

    if (isRosterLoading || authLoading) {
      // Wait until profile/grade is available
      setLoading(true);
      return;
    }

    if (validGrade === undefined) {
      setLoading(false);
      setScenes([]);
      setError('No grade is assigned to your account. Please contact your teacher/admin.');
      return;
    }

    listPictureTalkScenes({ grade: validGrade })
      .then((list) => {
        if (!cancelled) setScenes(list);
      })
      .catch(async (e) => {
        if (cancelled) return;
        if (e instanceof LearningHttpError) {
          if (
            await handleSchoolFeatureDisabledIfPresent({
              status: e.status,
              bodyText: e.bodyText,
              navigate,
              refetchFeatureAccess,
              fallbackFeatureKey: 'picture_talk',
            })
          ) {
            return;
          }
          setError(e.message);
          return;
        }
        setError(e instanceof Error ? e.message : 'Failed to load scenes.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, refetchFeatureAccess, grade, isRosterLoading, authLoading]);

  return (
    <Layout>
      <PageHeader title="Picture Talk" subtitle="காட்சிகளைத் தேர்ந்தெடுத்துப் பேசுப் பயிற்சி செய்யுங்கள்" />
      <main className="mx-auto max-w-5xl px-4 pb-10">
        {loading && (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        )}

        {!loading && error && (
          <p
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            {error}
          </p>
        )}

        {!loading && !error && scenes.length === 0 && (
          <p className="rounded-lg border border-app-border bg-app-surface-muted/50 px-4 py-8 text-center text-app-text-muted dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
            இப்போது காட்சிகள் எதுவும் இல்லை. பின்னர் முயற்சிக்கவும்.
          </p>
        )}

        {!loading && !error && scenes.length > 0 && (
          <ul className="grid gap-4 sm:grid-cols-2">
            {scenes.map((s) => {
              const title = displaySceneTitle(s);
              const preview =
                s.primary_image_url?.trim() ||
                undefined;
              return (
                <li key={s.scene_id}>
                  <button
                    type="button"
                    onClick={() => navigate(studentPictureTalkScenePath(s.scene_id))}
                    className="w-full text-left"
                  >
                    <Card className="h-full overflow-hidden transition-shadow hover:shadow-md">
                      <div className="aspect-video w-full overflow-hidden bg-slate-100 dark:bg-gray-900">
                        <ImageWithFallback
                          src={preview}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <CardContent className="pt-3">
                        <h2 className="text-lg font-semibold text-app-text dark:text-white">{title}</h2>
                      </CardContent>
                    </Card>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </Layout>
  );
};

export default PictureTalkListPage;
